import { IProjectRole } from "@kuika/kuika-cl-model";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { FolderTree } from "./folder-tree";
import { EditingState } from "./role-management-modal";
import { RoleRow } from "./role-row";

interface RoleListProps {
  roles: IProjectRole[];
  roleFolders: any[];
  search: string;
  expandedIds: string[];
  editing: EditingState | null;
  screens: any[];
  isRoleInAnyFolder: (roleId: string, folders: any[]) => boolean;
  onDragStart: (start: any) => void;
  onDragEnd: (result: any) => void;
  onToggleExpand: (id: string) => void;
  onAddRole: (folderId?: string) => void;
  onAddSubFolder: (parentFolderId: string) => void;
  onEditFolder: (folder: any) => void;
  onDeleteFolder: (folder: any) => void;
  onStartEditRole: (role: IProjectRole) => void;
  onConfirmEditRole: (role: IProjectRole) => void;
  onCancelEdit: () => void;
  onUpdateField: (field: keyof EditingState, value: string) => void;
  onDeleteRole: (role: IProjectRole) => void;
}

export const RoleList: React.FC<RoleListProps> = ({
  roles,
  roleFolders,
  search,
  expandedIds,
  editing,
  screens,
  isRoleInAnyFolder,
  onDragStart,
  onDragEnd,
  onToggleExpand,
  onAddRole,
  onAddSubFolder,
  onEditFolder,
  onDeleteFolder,
  onStartEditRole,
  onConfirmEditRole,
  onCancelEdit,
  onUpdateField,
  onDeleteRole
}) => {
  const unassignedRoles = roles
    .filter((r) => !isRoleInAnyFolder(r.id, roleFolders))
    .filter((r) => r.name.toLowerCase().includes(search.toLowerCase()) || !search);

  const hasFolders = roleFolders.length > 0;

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="role-management-list-container">
        <FolderTree
          folders={roleFolders}
          search={search}
          roles={roles}
          expandedIds={expandedIds}
          editing={editing}
          screens={screens}
          onToggleExpand={onToggleExpand}
          onAddRole={onAddRole}
          onAddSubFolder={onAddSubFolder}
          onEditFolder={onEditFolder}
          onDeleteFolder={onDeleteFolder}
          onStartEditRole={onStartEditRole}
          onConfirmEditRole={onConfirmEditRole}
          onCancelEdit={onCancelEdit}
          onUpdateField={onUpdateField}
          onDeleteRole={onDeleteRole}
        />
        <Droppable droppableId="droppable-root">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {unassignedRoles.map((role, index) => (
                <Draggable key={role.id} draggableId={role.id} index={index}>
                  {(p, snapshot) => (
                    <div
                      ref={p.innerRef}
                      {...p.draggableProps}
                      {...p.dragHandleProps}
                      className={snapshot.isDragging ? "dragging" : ""}
                      style={{
                        ...p.draggableProps.style,
                        opacity: snapshot.isDragging ? 0.5 : 1
                      }}
                    >
                      <RoleRow
                        role={role}
                        editing={editing}
                        screens={screens}
                        showDragHandle={hasFolders}
                        onStartEdit={onStartEditRole}
                        onConfirmEdit={onConfirmEditRole}
                        onCancelEdit={onCancelEdit}
                        onUpdateField={onUpdateField}
                        onDeleteRole={onDeleteRole}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </DragDropContext>
  );
};
